import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { HEADER_H, HEADER_TIER_H } from '../lib/constants'
import { xOf } from '../lib/geometry'
import type { Tick as TickData } from '../lib/header-tiers'
import TodayLine from './timeline/TodayLine'
import { useGanttLayout } from './gantt-layout'

// Review fix (Minor #16): `clipId` used to be `gantt-tick-clip-${y}-${x}` — unique within one
// header, but two headers on the same page (spec C's print view) or a fractional `x` at project
// zoom could collide. React 18 ships `useId`, but this project's `@types/react` is still pinned
// at 17 (no `useId` in its declarations) — a per-instance counter via `useRef` gives the same
// "stable for this mounted instance, unique on the page" guarantee without depending on a hook
// the types don't know about (same pattern as `panel/FieldRow.tsx`'s `useStableId`).
let timelineHeaderIdSeq = 0
const useTimelineHeaderInstanceId = (): string => {
  const ref = useRef<string | null>(null)
  if (!ref.current) ref.current = `gantt-header-${++timelineHeaderIdSeq}`
  return ref.current
}

interface TickProps extends Pick<TickData, 'x' | 'width' | 'label'> {
  y: number
  /** Clamped left edge (content coordinates) so the label sticks while the tick scrolls past. Top tier only. */
  sticky?: number
  instanceId: string
}

/** One header cell: a bordered box plus a label clipped to it (MS Project's sticky-tier behaviour, spec §8.2). */
const Tick: React.FC<TickProps> = ({ x, width, label, y, sticky, instanceId }) => {
  const w = Math.max(0, width)
  const clipId = `gantt-tick-clip-${instanceId}-${y}-${x}`
  return (
    <g>
      <clipPath id={clipId}>
        <rect x={x} y={y} width={w} height={HEADER_TIER_H} />
      </clipPath>
      <rect x={x} y={y} width={w} height={HEADER_TIER_H} className="fill-none stroke-border" />
      <text x={(sticky ?? x) + 4} y={y + HEADER_TIER_H / 2} clipPath={`url(#${clipId})`} dominantBaseline="middle" className="text-xs fill-muted-foreground">
        {label}
      </text>
    </g>
  )
}

/**
 * The two-tier timeline header (spec §8.2). Positioned inside the sticky header
 * row at `gridWidth + scrollLeft`; the inner group is translated by `-scrollLeft`
 * so children are drawn in timeline content coordinates.
 */
const GanttTimelineHeader: React.FC = () => {
  const { t } = useTranslation()
  const { range, pxPerDay, gridWidth, viewportWidth, scrollLeft, today, tiers } = useGanttLayout()
  const instanceId = useTimelineHeaderInstanceId()
  const width = Math.max(0, viewportWidth - gridWidth)
  const todayX = xOf(today, range.start, pxPerDay) + pxPerDay / 2

  return (
    <svg
      data-gantt-timeline-header
      className="absolute top-0 overflow-hidden border-b bg-background"
      // left/width track scroll and the splitter; height is the fixed HEADER_H, carried by the
      // `height` SVG attribute below rather than duplicated here.
      // ui-drift: dynamic
      style={{ left: gridWidth + scrollLeft, width }}
      width={width}
      height={HEADER_H}
    >
      <g transform={`translate(${-scrollLeft},0)`}>
        {tiers.top.map((tk) => (
          <Tick key={tk.key} x={tk.x} width={tk.width} label={tk.label} y={0} sticky={Math.max(tk.x, scrollLeft)} instanceId={instanceId} />
        ))}
        {tiers.bottom.map((tk) => (
          <Tick key={tk.key} x={tk.x} width={tk.width} label={tk.label} y={HEADER_TIER_H} instanceId={instanceId} />
        ))}
        <TodayLine x={todayX} y1={HEADER_H - 6} y2={HEADER_H} label={t('Schedule_today')} />
      </g>
    </svg>
  )
}

export default GanttTimelineHeader
